import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Check, X } from 'lucide-react';

const Step2PaymentMode = ({ selectedPlan, paymentMode, onSelectMode, pricing }) => {
  const { isDark } = useTheme();

  const planNames = {
    free: 'FREE',
    solo_vpn: 'SOLO VPN',
    solo_hotspot: 'SOLO HOTSPOT',
    solo_pppoe: 'SOLO PPPOE',
    hotspot_pppoe: 'HOTSPOT + PPPOE',
    todo_incluido: 'TODO INCLUIDO'
  };

  const planFeatures = {
    free: ['VPN', 'Routers Ilimitados', 'Vendedores Ilimitados', 'Vouchers', 'PPPoE', '30 días gratis'],
    solo_vpn: ['VPN', '1 Router incluido'],
    solo_hotspot: ['VPN', '1 Router', '3 Vendedores', 'Vouchers', 'Editor', 'Reportes'],
    solo_pppoe: ['VPN', '1 Router', '3 Vendedores', 'Gestión PPPoE'],
    hotspot_pppoe: ['VPN', '1 Router', '3 Vendedores', 'Vouchers', 'PPPoE', 'Reportes'],
    todo_incluido: ['VPN', '1 Router', '3 Vendedores', 'Vouchers', 'PPPoE', 'Portales', 'Todo incluido']
  };

  const getPrice = (mode) => {
    if (!pricing || selectedPlan === 'free') return 'GRATIS';
    const planPricing = pricing[selectedPlan];
    if (!planPricing) return 'N/A';
    
    const modeMap = {
      'monthly': 'monthly',
      'semiannual': 'semiannual',
      'annual': 'annual',
      'lifetime': 'lifetime'
    };
    
    const price = planPricing[modeMap[mode]];
    return price > 0 ? `$${price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : 'N/A';
  };

  const getPriceSuffix = (mode) => {
    const suffixes = {
      monthly: '/ mes',
      semiannual: '/ 6 meses',
      annual: '/ año',
      lifetime: 'pago único'
    };
    return suffixes[mode] || '';
  };

  const paymentModes = [
    { value: 'monthly', label: 'MENSUAL', icon: '📅', description: 'Pago cada mes' },
    { value: 'semiannual', label: 'SEMESTRAL', icon: '📆', description: 'Ahorra más' },
    { value: 'annual', label: 'ANUAL', icon: '💵', description: 'Máximo ahorro' },
    { value: 'lifetime', label: 'PAGO ÚNICO', icon: '💰', description: 'Sin renovaciones', promo: true }
  ];

  return (
    <div>
      <div className="text-center mb-10">
        <h2 className={`text-4xl font-extrabold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Selecciona tu Modalidad de Pago
        </h2>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Plan seleccionado: <span className="font-bold text-blue-600">{planNames[selectedPlan]}</span>
        </p>
      </div>

      {/* Resumen del plan */}
      <div className={`rounded-xl p-6 mb-8 ${
        isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'
      }`}>
        <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {planNames[selectedPlan]}
        </h3>
        <div className="flex flex-wrap gap-2">
          {planFeatures[selectedPlan]?.map((feature, index) => (
            <span
              key={index}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'
              }`}
            >
              ✓ {feature}
            </span>
          ))}
        </div>
      </div>

      {/* Modalidades de pago */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {paymentModes.map((mode) => (
          <button
            key={mode.value}
            onClick={() => onSelectMode(mode.value)}
            className={`relative p-6 rounded-xl font-bold text-lg transition-all transform hover:scale-105 ${
              paymentMode === mode.value
                ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-xl ring-4 ring-blue-400 scale-105'
                : isDark
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {mode.promo && paymentMode !== mode.value && (
              <span className="absolute -top-2 -right-2 px-3 py-1 bg-red-600 text-white rounded-full text-xs font-bold animate-pulse">
                ¡OFERTA!
              </span>
            )}
            <div className="text-center">
              <div className="text-2xl mb-2">{mode.icon}</div>
              <div>{mode.label}</div>
              <div className={`text-sm mt-1 ${
                paymentMode === mode.value ? 'opacity-90' : 'opacity-80'
              }`}>
                {mode.description}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Precio en tiempo real */}
      {paymentMode && (
        <div className={`rounded-2xl p-8 text-center ${
          paymentMode === 'lifetime'
            ? 'bg-gradient-to-r from-yellow-400 to-orange-400'
            : isDark
            ? 'bg-gradient-to-br from-blue-900 to-purple-900'
            : 'bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200'
        }`}>
          <div className={`text-sm font-medium mb-2 ${
            paymentMode === 'lifetime' ? 'text-gray-800' : isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Precio {paymentMode === 'monthly' ? 'Mensual' : paymentMode === 'semiannual' ? 'Semestral' : paymentMode === 'annual' ? 'Anual' : 'de Pago Único'}
          </div>
          <div className={`text-6xl font-extrabold mb-2 ${
            paymentMode === 'lifetime' ? 'text-gray-900' : isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {getPrice(paymentMode)}
          </div>
          <div className={`text-lg ${
            paymentMode === 'lifetime' ? 'text-gray-800 font-medium' : isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {getPriceSuffix(paymentMode)}
          </div>
          {selectedPlan === 'free' && (
            <div className={`mt-4 text-sm font-medium ${
              isDark ? 'text-green-400' : 'text-green-600'
            }`}>
              30 días de prueba gratuita
            </div>
          )}
        </div>
      )}

      {/* Mensaje de ayuda */}
      {!paymentMode && (
        <div className={`text-center p-4 rounded-xl ${
          isDark ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'
        }`}>
          <p className={`text-lg font-semibold ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
            👆 Selecciona una modalidad de pago para continuar
          </p>
        </div>
      )}
    </div>
  );
};

export default Step2PaymentMode;
